import { useState } from "react";

import { appstr } from "@coreapp/utils/appstr";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import { BigInfoIcon, Button, Flex, Icon, Text } from "@theorgchartdev/storybook";
import type { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import type { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { useNavigationContext } from "@/providers/NavigationProvider";
import { EmployeeBaseballCard, FavoriteManagerInterface } from "./EmployeeBaseballCard";
import { HeadingConfigType } from "../../Modals/SettingsModal/ChartSettings/ProfileConfiguration/ProfileConfiguration.utils";

type Props = {
  doc: QXDoc;
  dataObj: Nullish<QObjDBObject>;
  record: Record<string, string>;
  setShowMoreFields: React.Dispatch<React.SetStateAction<boolean | null>>;
  headingConfig: HeadingConfigType;
  employeeCardProps: {
    favoriteController: FavoriteManagerInterface;
    visibleFields: string[];
  };

  mapping: Record<string, string>;
};

export const BaseballCardHolders = (props: Props) => {
  // This state is only relevant for Positions with a single holder
  const [singleHolderShowMoreFields, setSingleHolderShowMoreFields] = useState<null | boolean>(
    null
  );

  const [showMoreAssignees, setShowMoreAssignees] = useState(false);
  const { LaunchUIElement } = useNavigationContext();

  const holders = props.dataObj?.GetHolderList() ?? [];

  const visibleHolders = showMoreAssignees
    ? holders
    : holders.slice(0, Math.min(5, holders.length));

  return (
    <Flex
      direction="column"
      gap="16px"
      width={"100%"}
      divProps={{ id: "ocn-profile-card-holders-section" }}
    >
      <Flex direction="row" justifyContent="flex-start" alignItems="center" gap="8px">
        <Text size="medium">{appstr("Assignees")}</Text>
        <Text className="ocn-badge-info-number">{holders.length}</Text>
      </Flex>
      {visibleHolders.length === 0 && (
        <BigInfoIcon color="brand" name="user" message={appstr("No assignees yet")} />
      )}
      {visibleHolders.length === 1 ? (
        <EmployeeBaseballCard
          isPositionChart={true}
          headingConfig={props.headingConfig}
          dataObj={visibleHolders[0]}
          record={visibleHolders[0].JSON()}
          mapping={props.mapping}
          showMoreFields={singleHolderShowMoreFields}
          setShowMoreFields={setSingleHolderShowMoreFields}
          {...props.employeeCardProps}
        />
      ) : (
        visibleHolders.map((holder, index) => {
          const holderName = sanitizeBlockedValue(holder.SmartGetFieldValue("Name"), "--");

          return (
            <Button
              key={index}
              modifier="tertiary"
              isFullWidth
              onClick={() => {
                const canvasRef = props.doc.GetCanvasRef();
                canvasRef?.ClearSelection();

                props.setShowMoreFields(true);
                LaunchUIElement("profile", true, {
                  target: holder
                });
              }}
            >
              <Flex direction="row" justifyContent="space-between" alignItems="center" gap="4px">
                <Flex direction="row" gap="8px" alignItems="center">
                  <div className="oc-profile-holder-icon">
                    <Icon name="user" size="small" />
                  </div>
                  <Text>{`${holderName}`}</Text>
                </Flex>
                <Icon name="chevron-right" size="small" />
              </Flex>
            </Button>
          );
        })
      )}
      {holders.length > 5 && (
        <Button
          dataTestId="profile-card-more-toggle"
          onClick={() => setShowMoreAssignees(prevShowMoreAssignees => !prevShowMoreAssignees)}
          size={"small"}
          modifier="tertiary"
          iconProps={{
            name: showMoreAssignees ? "square-minus" : "square-plus",
            size: "xsmall"
          }}
        >
          {showMoreAssignees
            ? appstr("Show less entity", { entity: appstr("Assignees").toLocaleLowerCase() })
            : appstr("Show more entity", { entity: appstr("Assignees").toLocaleLowerCase() })}
        </Button>
      )}
    </Flex>
  );
};
